import type {Content} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import {useRouter} from 'next/router'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useDebounce, useWindowSize} from 'react-use'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {CustomSelect} from 'slices/IntegrationsContainer/CustomSelect'
import {ButtonV2} from 'src/common/ButtonV2'
import type {locales} from 'src/common/constants'
import {BREAKPOINTS, PRODUCT_ANNOUNCEMENTS_CATEGORIES} from 'src/common/constants'
import {Container} from 'src/common/Container'
import {Headline} from 'src/common/Headline'
import {EVENTS, track} from 'src/common/helpers'
import {Icon} from 'src/common/Icon'
import type {Context} from 'src/common/types'
import {ProductAnnouncementCard} from './ProductAnnouncementCard'

const MAX_VISIBLE_CARDS_DESKTOP = 8
const MAX_VISIBLE_CARDS_MOBILE = 5

const getAnnouncementCategories = (locale: keyof typeof locales) => {
  const categoriesSet = PRODUCT_ANNOUNCEMENTS_CATEGORIES[locale]
  const result = []

  for (const [, value] of Object.entries(categoriesSet)) {
    result.push({category_name: value})
  }

  return {
    categoriesOptions: result,
    categoriesSet,
  }
}

/**
 * Props for `ProductAnnouncementsContainer`.
 */
export type ProductAnnouncementsContainerProps = SliceComponentProps<Content.ProductAnnouncementsContainerSlice> & {
  context: Context
}

/**
 * Component for "ProductAnnouncementsContainer" Slices.
 */
const ProductAnnouncementsContainer = ({
  slice,
  context,
  index: componentIndex,
}: ProductAnnouncementsContainerProps): JSX.Element => {
  const [filteredCards, setFilteredCards] = useState(context.product_announcements_data || [])
  const router = useRouter()
  const locale = (router.locale as keyof typeof locales) || 'en-us'
  const [searchText, setSearchText] = useState('')
  const [selectedCategory, setSelectedCategory] = useState(0)
  const [showAll, setShowAll] = useState(false)
  const selectContainerRef = useRef<HTMLDivElement>(null)
  const isMobile = useWindowSize().width < BREAKPOINTS.TABLET

  const {categoriesOptions, categoriesSet} = useMemo(() => getAnnouncementCategories(locale), [locale])

  const [,] = useDebounce(
    () => {
      setFilteredCards(
        context.product_announcements_data!.filter((announcement) => {
          const text = announcement.data.title! + announcement.data.description || ''
          return text.toLowerCase().includes(searchText.toLowerCase())
        }),
      )

      track({
        event: EVENTS.TEXT_INPUT,
        mktgId: 'search-bar',
        dataType: searchText,
        pageName: context.pageUid,
        location: `component-${componentIndex}`,
      })
    },
    2000,
    [searchText],
  )

  // Filter cards based on selected category
  useEffect(() => {
    if (selectedCategory === 0) {
      setFilteredCards(context.product_announcements_data!)
    } else {
      setFilteredCards(
        context.product_announcements_data!.filter(
          (announcement) =>
            announcement.data.category?.toUpperCase().replaceAll(' ', '_') ===
            categoriesOptions[selectedCategory].category_name.toUpperCase().replaceAll(' ', '_'),
        ),
      )
    }
  }, [selectedCategory, context.product_announcements_data, categoriesOptions])

  // Calculate the cards to display (first 8 or all if showAll is true)
  const visibleCards = useMemo(() => {
    if (showAll) {
      return filteredCards
    }

    if (isMobile) {
      return filteredCards.slice(0, MAX_VISIBLE_CARDS_MOBILE)
    }

    return filteredCards.slice(0, MAX_VISIBLE_CARDS_DESKTOP)
  }, [filteredCards, showAll, isMobile])

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="!overflow-visible !py-10 md:px-5 lg:!pt-[92px] lg:pb-20 xl:px-0"
    >
      <div className="grid-base w-full gap-y-6">
        <div className="col-span-full px-5 md:px-10 md:pb-2 lg:px-5 xl:px-0">
          <h2 className="text-h3 text-moss-black">{slice.primary.title}</h2>
        </div>
        <div className="relative col-span-4 flex flex-col flex-wrap gap-2 px-5 md:col-span-8 md:flex-row md:flex-nowrap lg:max-w-[572px] lg:px-0">
          <div className="relative flex md:w-[282px]">
            <input
              className="
            text-tg-body-m
            h-10
            w-full
            rounded-[4px]
            border
            border-black/[10%]
            py-2.5
            !pl-11
            !pr-3
            text-black
            placeholder:text-moss-grey-200
            hover:bg-grey-110
            focus:outline
            focus:outline-1
            focus:outline-[#94CC32]
            lg:w-[520px]
            "
              placeholder={slice.primary.search_placeholder || ''}
              value={searchText}
              onChange={({currentTarget}) => {
                setSearchText(currentTarget.value)
              }}
            />
            <Icon name="search" className="absolute left-4 top-3 size-4 text-moss-grey-400" />
          </div>
          <div className="text-body-m w-full text-grey-300 md:w-[260px]" ref={selectContainerRef}>
            <CustomSelect
              options={categoriesOptions}
              activeIndex={selectedCategory}
              setActiveIndex={setSelectedCategory}
              trackingData={{
                mktgId: 'product-announcements-category-select',
                pageName: context.pageUid,
                dataType: 'product-announcements-category-select',
              }}
              v2
            />
          </div>
        </div>
        <div className=" col-span-full flex min-h-[658px] flex-col gap-10 px-5 pt-4 md:gap-20 md:pt-10 lg:px-0 lg:pt-[68px]">
          {filteredCards.length ? (
            <>
              {visibleCards.map((announcement, index) => {
                const category = announcement.data.category?.toUpperCase().replace(/ /g, '_')
                const categoryLabel = categoriesSet[category as keyof typeof categoriesSet]

                const formattedDate = new Date(announcement.data.date!).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })

                return (
                  <ProductAnnouncementCard
                    key={index}
                    categoryLabel={categoryLabel}
                    location={`component-${componentIndex}-media-card-${index}`}
                    pageName={context.pageUid}
                    formattedDate={formattedDate}
                    {...announcement.data}
                  />
                )
              })}
              {filteredCards.length > 8 && !showAll && (
                <ButtonV2
                  trackingData={{
                    mktgId: 'product-announcements-show-more',
                    location: `component-${componentIndex}`,
                    pageName: context.pageUid,
                    dataType: 'product-announcements-show-more',
                  }}
                  onClick={() => setShowAll(true)}
                  variant="primary"
                  className="w-fit self-center"
                >
                  {slice.primary.show_more_button_label || 'Show more'}
                </ButtonV2>
              )}
            </>
          ) : (
            <Headline
              title={slice.primary.no_results_title}
              headingLevel="h3"
              subtext={slice.primary.no_results_subtext}
            />
          )}
        </div>
      </div>
    </Container>
  )
}

export default ProductAnnouncementsContainer
